import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { TFunction, useTranslation } from 'react-i18next';
import { DateDropdown, SearchInput } from '@dev/ee-components';
import { JourneyStatus } from '@dev/ee-api/ee-api-models';

import {
    DEFAULT_OFFSET,
    DEFAULT_PER_PAGE_COUNT,
    LONG_DATE_FORMAT,
    TIME_RANGE_MONTH,
    TIME_RANGE_SIX_MONTHS,
    TIME_RANGE_THREE_MONTHS,
    TIME_RANGE_TWO_WEEKS,
    TIME_RANGE_WEEK,
    TIME_RANGE_YEAR,
} from '@/services/constants';
import { GetAllAutomationsStart, getAutomationStore } from '@/store/automations/actions';
import dayjs from '@/services/tools/dayjs';
import { debounce } from '@/services/tools/debounce';
import { DateDropdownOptions } from '@/services/dictionaries/DateRangeValues';
import { calculateOffset } from '@/services/tools/handleUrlParams';
import { GetAutomationsParams } from '@/services/ElasticApi';
import { useDateDropdownValue } from '@/services/hooks/useDateDropdownValue';
import { DateOptionsInterface } from '../../../AutomationsInterface';

const SearchInputWrapper = styled.div`
    width: 280px;
`;
const TableFilters = styled.div`
    display: flex;
    flex-direction: row;
    padding: 0px 48px 34px;
    justify-content: space-between;
`;

const dateFilterData = (t: TFunction): DateOptionsInterface[] => [
    { label: t('specifictimerange'), value: DateDropdownOptions.Calendar },
    { label: t('last7days'), value: TIME_RANGE_WEEK },
    { label: t('last14days'), value: TIME_RANGE_TWO_WEEKS },
    { label: t('last30days'), value: TIME_RANGE_MONTH },
    { label: t('last3months'), value: TIME_RANGE_THREE_MONTHS },
    { label: t('last6months'), value: TIME_RANGE_SIX_MONTHS },
    { label: t('lastyear'), value: TIME_RANGE_YEAR },
    { label: t('all'), value: DateDropdownOptions.All },
];

export const TableFiltersComponent = (): JSX.Element => {
    const { t } = useTranslation();
    const automations = useSelector(getAutomationStore);
    const dispatch = useDispatch();

    const perPage = automations?.pagination?.perPage ?? DEFAULT_PER_PAGE_COUNT;
    const page = automations?.pagination?.page ?? 1;
    const from = automations?.pagination?.from;
    const to = automations?.pagination?.to;
    const search = automations?.pagination?.name;
    const statuses = automations?.pagination?.statuses ?? [
        JourneyStatus.Invalid,
        JourneyStatus.Active,
        JourneyStatus.Paused,
    ];
    const dateDropdownValue = useDateDropdownValue(false);

    const offset = calculateOffset(page, perPage);

    const requestParams: GetAutomationsParams = {
        from,
        to,
        statuses,
        name: search,
        limit: perPage,
        offset,
    };

    return (
        <TableFilters>
            <DateDropdown
                options={dateFilterData(t)}
                defaultPlaceholder={t('all')}
                type="input"
                returnRawData
                value={dateDropdownValue}
                calendarOnChange={(startDate?: string, endDate?: string) => {
                    dispatch(
                        GetAllAutomationsStart({
                            ...requestParams,
                            to: endDate,
                            from: dayjs(startDate).startOf('day').format(),
                            offset: DEFAULT_OFFSET,
                        })
                    );
                }}
                onItemClick={(ev, value?: Record<string, string | number>) => {
                    dispatch(
                        GetAllAutomationsStart({
                            ...requestParams,
                            to: dayjs().endOf('day').format(LONG_DATE_FORMAT),
                            from:
                                value?.value === DateDropdownOptions.All
                                    ? null
                                    : dayjs()
                                          .subtract(value?.value as number, 'day')
                                          .startOf('day')
                                          .format(LONG_DATE_FORMAT),
                            offset: DEFAULT_OFFSET,
                        })
                    );
                }}
                buttonVariant="tertiary"
                startDate={from?.toString()}
                endDate={to?.toString()}
            />
            <SearchInputWrapper>
                <SearchInput
                    id="searchAutomations"
                    name="search"
                    placeholder={t('searchforautomations')}
                    type="text"
                    onChange={(event): void => {
                        const searchString = event?.target?.value;
                        debounce((): void => {
                            dispatch(
                                GetAllAutomationsStart({
                                    ...requestParams,
                                    name: searchString,
                                    offset: DEFAULT_OFFSET,
                                })
                            );
                        }, 500);
                    }}
                    onDelete={(): void => {
                        dispatch(
                            GetAllAutomationsStart({
                                ...requestParams,
                                name: '',
                                offset: DEFAULT_OFFSET,
                            })
                        );
                    }}
                    defaultValue={search}
                />
            </SearchInputWrapper>
        </TableFilters>
    );
};
